import React, { useCallback } from "react";

import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import moment from "moment";
import { type CheckboxProps, Checkbox as SemanticCheckbox } from "semantic-ui-react";

import MicrosoftTodoLogo from "~/integrations/microsoftTodo/MicrosoftTodoLogo";
import { Button } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import { IconButton } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import type { MicrosoftTodoListType } from "./MicrosoftTodoIntegration";
import MicrosoftTodoRemoveListModal from "./MicrosoftTodoRemoveListModal";

import settingsStyle from "~/settings/css/settings.module.css";

import styles from "./MicrosoftTodo.module.css";

type MicrosoftTodoListSettingsProps = {
	list: MicrosoftTodoListType | undefined;
	onChangeList: () => void;
	onRemoveList: () => void;
	onToggleSync: (e: React.FormEvent, data: CheckboxProps) => void;
	onSyncActionItems: () => void;
	syncPending: boolean;
	syncEnabled: boolean;
	syncedCount: number;
	unsyncedCount: number;
	lastSyncDate: string | undefined;
};

export default function MicrosoftTodoListSettings(props: MicrosoftTodoListSettingsProps) {
	const {
		list,
		onChangeList,
		onRemoveList,
		onToggleSync,
		onSyncActionItems,
		syncEnabled,
		syncPending,
		syncedCount,
		unsyncedCount,
		lastSyncDate,
	} = props;

	const onRemoveListClicked = useCallback(async () => {
		const result = await __globalModalContext?.open<boolean>(({ close }) => (
			<MicrosoftTodoRemoveListModal onClose={close} listName={list?.name ?? ""} />
		));
		if (!result) return;
		onRemoveList();
	}, [list?.name, onRemoveList]);

	return (
		<>
			<div className={styles.listHeader}>
				<div className={styles.listTitle}>
					<MicrosoftTodoLogo className={styles.logo} />
					<a
						href={"https://to-do.office.com/tasks"}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.headerText}
					>
						{list?.name}
					</a>
					{syncPending && <div className={styles.connectionStatusLabel}>Sync in progress…</div>}
					{!syncPending && syncEnabled && (
						<div className={cx(styles.connectionStatusLabel, styles.activeLabel)}>Active</div>
					)}
					{!syncPending && !syncEnabled && (
						<div className={cx(styles.connectionStatusLabel, styles.inactiveLabel)}>Inactive</div>
					)}
				</div>
				{!syncPending && (
					<div className={styles.optionsContainer}>
						<span className={styles.toggleLabel}>Enable action items to sync</span>

						<SemanticCheckbox
							toggle
							checked={syncEnabled}
							onChange={onToggleSync}
							className={cx(settingsStyle.settingsPageToggle, styles.toggle)}
						/>
						<ArrowDropdown
							heading="List actions"
							popupClassName={styles.flyoutMenu}
							trigger={<IconButton className={styles.listMenu} title="More actions" icon={faEllipsisV} />}
						>
							<DropdownItem key="change" onClick={onChangeList} className={styles.dropdownItem}>
								Change list…
							</DropdownItem>

							<DropdownItem key="disconnect" onClick={onRemoveListClicked} className={styles.dropdownItem}>
								Remove list…
							</DropdownItem>
						</ArrowDropdown>
					</div>
				)}
			</div>
			{!syncPending && (
				<div className={styles.syncSection}>
					<span>
						Manually sync <b>({unsyncedCount})</b> incomplete action items to Microsoft To Do
					</span>
					<Button size="normal" variant="primary" disabled={unsyncedCount === 0} onClick={onSyncActionItems}>
						Sync
					</Button>
				</div>
			)}

			<div className={styles.userStats}>
				<ul>
					{syncPending && unsyncedCount === 1 && (
						<li>
							Syncing <b>({unsyncedCount})</b> incomplete action item with To Do. This may take a few moments.
						</li>
					)}
					{syncPending && unsyncedCount !== 1 && (
						<li>
							Syncing <b>({unsyncedCount})</b> incomplete action items with To Do. This may take a few moments.
						</li>
					)}
					{!syncPending && lastSyncDate && (
						<li>Last item sent to this To Do list: {moment(lastSyncDate).format("dddd, MMMM Do [at] h:mm a")}</li>
					)}
					{!syncPending && syncedCount == 1 && <li>{syncedCount} tasks sent to this To Do list to date</li>}
					{!syncPending && syncedCount != 1 && <li>{syncedCount} tasks sent to this To Do list to date</li>}
				</ul>
			</div>
		</>
	);
}
